import React, { useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ListItem,
  List,
  ListItemText,
  useMediaQuery,
  ListItemSecondaryAction,
  Switch,
  ListSubheader,
  Select,
  MenuItem,
  IconButton,
  Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { AccountCircle, ArrowForwardIos, ContactMail, ExitToApp, Help } from '@material-ui/icons';
import { useHistory, useLocation } from 'react-router-dom';
import EmployeeInfoContext from '../../contexts/EmployeeInfoContext';
import { Claim, deleteClaimForUser, putClaim, impersonate, unImpersonate, Employee } from '../../utils/AccountsAPI';
import { globalAny } from './NavbarWithSidebar';
import { cloneDeep } from 'lodash';
import { toastError } from '../../utils/Functions';
import ToastContext from '../../contexts/ToastContext';
import { storeBaseURL, getBaseUrlName } from '../../utils/AxiosConfig';
import { UserAutocomplete } from '../../components/UserAutocomplete';
import { useUsersContext } from '../../hooks/useUsersContext';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
  backButton: { position: 'absolute', left: theme.spacing(1), top: theme.spacing(1) },
  backIcon: { transform: 'rotate(.5turn) translateX(2px)' },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    textAlign: 'center',
  },
  dialogContent: {
    display: 'flex',
    padding: theme.spacing(1) + 'px 0',
  },
  centerContent: {
    width: '100%',
  },
  partData: {
    padding: theme.spacing(1),
    border: '1px solid rgba(0,0,0,.24)',
    margin: theme.spacing(2),
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
    width: '100%',
  },
  listItem: {
    width: '100%',
    display: 'inline-flex',
  },
  listItemStatus: {
    padding: theme.spacing(1),
    paddingRight: 0,
    paddingLeft: theme.spacing(2),
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '20% 30% 10% 40%',
  },
  listItemLabel: {
    padding: '4px 0',
  },
  listItemField: {
    margin: 'auto',
    marginRight: 0,
    width: 'min(150px,70%)',
    textAlign: 'right',
  },
  listIcon: {
    margin: 'auto',
    marginRight: 0,
    color: theme.palette.grey[500],
  },
  formControl: {
    marginBottom: theme.spacing(2),
  },
  formControlEarningsType: {
    margin: 'auto',
    marginRight: 0,
    width: 'min(150px,70%)',
  },
  bold: {
    fontWeight: 'bold',
  },
  autocomplete: {},
  listItemImpersonate: { height: '72px' },
}));

export default function SettingsDialog(props) {
  const classes = useStyles(props);
  const theme = useTheme();
  const [employeeInfo, setEmployeeInfo] = useContext(EmployeeInfoContext);
  const toast = useContext(ToastContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  const [users] = useUsersContext();

  const history = useHistory();
  const location = useLocation();

  const goToLocation = (location) => (e) => {
    if (isMobile) props.onClose();
    history.push(location);
  };

  const onAPISwitch = (e) => {
    storeBaseURL(e.target.value);
    history.replace(location.pathname, location.state);
  };

  const onRoleChange = (event) => {
    const values = event.target.value;
    const clone = cloneDeep(employeeInfo);
    clone?.setRoles(values);
    setEmployeeInfo(clone);
  };

  const toggleWeekEnding = () => {
    if (!employeeInfo) return;

    const claim = { claimType: 'defaultPreviousWeekEnding', claimValue: 'true' } as Claim;
    let promise;
    var prevEmpInfo = employeeInfo;

    const clone = cloneDeep(employeeInfo);

    let existingClaim = employeeInfo?.getClaim('defaultPreviousWeekEnding');

    if (existingClaim) {
      promise = deleteClaimForUser(employeeInfo.id, 'defaultPreviousWeekEnding');
      clone.removeClaim(existingClaim.id);
      setEmployeeInfo(clone);
    } else {
      promise = putClaim(employeeInfo?.id, claim).then((c) => {
        clone.addClaim(c);
        setEmployeeInfo(clone);
      });
    }

    promise.catch((e) => {
      setEmployeeInfo(prevEmpInfo);
      toastError(toast, e);
    });
  };

  const onRevertImpersonate = (e) => {
    unImpersonate()
      .then((response) => setEmployeeInfo(undefined))
      .catch((err) => toastError(toast, err));
  };

  const onSelectUser = (e, value) => {
    if (value?.id)
      impersonate(value?.id)
        .then((response) => setEmployeeInfo(new Employee({ isImpersonating: true } as Employee)))
        .catch((err) => toastError(toast, err));
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} fullScreen={isMobile} fullWidth maxWidth='sm'>
      <DialogTitle id='settingsDialogTitle' className={classes.dialogTitle}>
        <IconButton className={classes.backButton} onClick={props.onClose}>
          <ArrowForwardIos className={classes.backIcon} />
        </IconButton>
        <span style={{ margin: 'auto', fontSize: 'inherit' }}>Settings</span>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <List className={classes.list}>
          {/* Account */}
          {employeeInfo ? (
            <>
              <ListSubheader disableSticky>Account</ListSubheader>
              <ListItem button onClick={goToLocation('/Account')} className={classes.listItem}>
                <ListItemText primary='My Account' />
                <ListItemSecondaryAction>
                  <AccountCircle />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem
                button
                divider
                onClick={() => {
                  props.onClose();
                  props.handleLogout();
                }}
                className={classes.listItem}
              >
                <ListItemText primary='Log Out' />
                <ListItemSecondaryAction>
                  <ExitToApp />
                </ListItemSecondaryAction>
              </ListItem>
            </>
          ) : null}

          {/* Settings */}
          <ListSubheader disableSticky>Settings</ListSubheader>
          {/* <ListItem button divider onClick={props.toggleTheme} className={classes.listItem}>
            <ListItemText primary='Use Dark Theme' />
            <ListItemSecondaryAction>
              <Switch edge='end' onChange={props.toggleTheme} checked={theme.palette.type === 'dark'} />
            </ListItemSecondaryAction>
          </ListItem> */}
          {employeeInfo ? (
            <ListItem button divider onClick={toggleWeekEnding} className={classes.listItem}>
              <ListItemText primary='Default Previous Week Ending' />
              <ListItemSecondaryAction>
                <Switch edge='end' onChange={toggleWeekEnding} checked={employeeInfo?.hasClaim('defaultPreviousWeekEnding', 'true') ?? false} />
              </ListItemSecondaryAction>
            </ListItem>
          ) : null}

          {/* Admin Settings */}
          {employeeInfo?.wasInRole('Admin') || employeeInfo?.isImpersonating ? (
            <>
              <ListSubheader disableSticky>Admin</ListSubheader>
              <ListItem divider className={classes.listItem}>
                <ListItemText primary='API' />
                <ListItemSecondaryAction style={{ minWidth: '45%' }}>
                  <Select native fullWidth value={getBaseUrlName()} onChange={onAPISwitch}>
                    <option value='Live'>Live</option>
                    <option value='Local'>Local</option>
                    <option value='Test'>Test</option>
                  </Select>
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem divider className={classes.listItem}>
                <ListItemText primary='Role(s)' />
                <ListItemSecondaryAction style={{ minWidth: '45%' }}>
                  <Select multiple fullWidth value={employeeInfo.strRoles} onChange={onRoleChange}>
                    <MenuItem value='admin'>Admin</MenuItem>
                    <MenuItem value='accounting'>Accounting</MenuItem>
                    <MenuItem value='manager'>Manager</MenuItem>
                  </Select>
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem divider className={clsx(classes.listItem, classes.listItemImpersonate)}>
                <ListItemText primary='Impersonation' />
                <ListItemSecondaryAction style={{ minWidth: '45%' }}>
                  {employeeInfo.isImpersonating ? (
                    <Button onClick={employeeInfo?.isImpersonating ? onRevertImpersonate : undefined}>
                      <Typography>Revert</Typography>
                    </Button>
                  ) : (
                    <UserAutocomplete
                      className={classes.autocomplete}
                      options={users}
                      value={null}
                      onChange={onSelectUser}
                      placeholder='Users'
                      color='secondary'
                      autoComplete
                      TextFieldProps={{ size: 'small' }}
                    />
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            </>
          ) : null}

          {/* About */}
          <ListSubheader disableSticky>About</ListSubheader>
          {/* <ListItem button onClick={() => window.open('https://sdsma.formstack.com/forms/sds_support_request', '_blank')} className={classes.listItem}> */}
          <ListItem button onClick={() => window.open('https://sdsma.on.spiceworks.com/portal', '_blank')} className={classes.listItem}>
            <ListItemText primary='Contact Us' />
            <ListItemSecondaryAction>
              <ContactMail />
            </ListItemSecondaryAction>
          </ListItem>
          <ListItem button onClick={() => window.open('https://sdsma.com/tips', '_blank')} className={classes.listItem}>
            <ListItemText primary='Help' />
            <ListItemSecondaryAction>
              <Help />
            </ListItemSecondaryAction>
          </ListItem>
          <ListItem className={classes.listItem}>
            <ListItemText primary={'TIPS - ' + globalAny.appVersion} />
            <ListItemSecondaryAction>
              <img alt='SDS' src={'favicon.png'} width='30' height='30' className='d-inline-block align-top' />
            </ListItemSecondaryAction>
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} color='primary'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
